import type {
    IAcademicFeeLineItem,
    IFeeItemsStagePatch,
    IS1BoardCardFeeSource,
    IStageData,
} from '../models/aggregated-lead.model'
import type { ILeadBoardGradeRowValue } from '../models/lead-info.model'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { roundMoneyParsedNumber } from './academic-fee-input.util'

export function isPositiveOrganisationAcademicFeeId(value: unknown): boolean {
    if (value == null || value === '') return false
    const parsedId = Number(value)
    return Number.isInteger(parsedId) && parsedId > 0
}

export function parseCurriculumIdFromUnknown(value: unknown): number | null {
    if (value == null || value === '') return null
    const parsedId = Number(value)
    return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null
}

/**
 * Resolves curriculum_id from S1 board cards (annual fee context).
 */
export function resolvePrimaryCurriculumIdFromS1BoardCards(
    boardCards: IS1BoardCardFeeSource[],
    getCurriculumIdByBoardName?: (boardName: string) => number | null,
): number | null {
    for (const boardCard of boardCards) {
        const curriculumFromCard = parseCurriculumIdFromUnknown(boardCard.curriculum_id)
        if (curriculumFromCard != null) {
            return curriculumFromCard
        }
        const boardName = String(boardCard.board || '').trim()
        if (boardName && getCurriculumIdByBoardName) {
            const curriculumFromBoard = getCurriculumIdByBoardName(boardName)
            if (curriculumFromBoard != null) {
                return curriculumFromBoard
            }
        }
    }
    return null
}

export function normalizeFeeItemGradesForApi(grades: string): string {
    return String(grades ?? '')
        .split(',')
        .map((gradeSegment) => gradeSegment.replace(REGEX_PATTERNS.WHITESPACE_GLOBAL, '').trim())
        .filter(Boolean)
        .join(',')
}

export function mapFeeItemsForStageApiWire(items: IAcademicFeeLineItem[]): IAcademicFeeLineItem[] {
    return items.map((feeItem) => {
        const mappedLine: Record<string, unknown> = {
            fee_type: feeItem.fee_type,
            grades: normalizeFeeItemGradesForApi(feeItem.grades),
            amount: feeItem.amount,
        }
        if (feeItem.fee_type === 'annual_fee') {
            mappedLine['curriculum_id'] = feeItem.curriculum_id
        }
        if (isPositiveOrganisationAcademicFeeId(feeItem.organisation_academic_fee_id)) {
            mappedLine['organisation_academic_fee_id'] = Number(
                feeItem.organisation_academic_fee_id,
            )
        }
        return mappedLine as unknown as IAcademicFeeLineItem
    })
}

function feeItemWireCompareKey(line: IAcademicFeeLineItem): string {
    const comparePayload: Record<string, unknown> = {
        fee_type: line.fee_type,
        grades: normalizeFeeItemGradesForApi(line.grades),
        amount: Number(line.amount),
    }
    if (line.fee_type === 'annual_fee') {
        comparePayload['curriculum_id'] = line.curriculum_id
    }
    return JSON.stringify(comparePayload)
}

/** Stable row identity when multiple grades share one organisation_academic_fee_id. */
function feeItemDiffLookupKey(line: IAcademicFeeLineItem): string {
    if (isPositiveOrganisationAcademicFeeId(line.organisation_academic_fee_id)) {
        return [
            line.organisation_academic_fee_id,
            line.fee_type,
            normalizeFeeItemGradesForApi(line.grades),
        ].join('::')
    }
    return feeItemWireCompareKey(line)
}

function parseFeeItemGradeNumbers(grades: string): number[] {
    return normalizeFeeItemGradesForApi(grades)
        .split(',')
        .map((grade) => Number(grade))
        .filter((grade) => Number.isFinite(grade))
}

function buildGradeToLineMap(items: IAcademicFeeLineItem[]): Map<number, IAcademicFeeLineItem> {
    const gradeToLine = new Map<number, IAcademicFeeLineItem>()
    for (const item of items) {
        for (const grade of parseFeeItemGradeNumbers(item.grades)) {
            gradeToLine.set(grade, item)
        }
    }
    return gradeToLine
}

function groupItemsByOrganisationFeeId(
    items: IAcademicFeeLineItem[],
): Map<number, IAcademicFeeLineItem[]> {
    const groups = new Map<number, IAcademicFeeLineItem[]>()
    for (const item of items) {
        if (!isPositiveOrganisationAcademicFeeId(item.organisation_academic_fee_id)) continue
        const orgId = Number(item.organisation_academic_fee_id)
        const bucket = groups.get(orgId) ?? []
        bucket.push(item)
        groups.set(orgId, bucket)
    }
    return groups
}

function buildPostFeeLine(
    template: IAcademicFeeLineItem,
    grade: number,
    amount: number,
): IAcademicFeeLineItem {
    return {
        fee_type: template.fee_type,
        grades: String(grade),
        amount,
        curriculum_id: template.curriculum_id ?? null,
    }
}

function buildPatchFeeLine(
    orgId: number,
    template: IAcademicFeeLineItem,
    grades: number[],
    amount: number,
): IAcademicFeeLineItem {
    return {
        organisation_academic_fee_id: orgId,
        fee_type: template.fee_type,
        grades: grades
            .slice()
            .sort((left, right) => left - right)
            .join(','),
        amount,
        curriculum_id: template.curriculum_id ?? null,
    }
}

function processBundledOrganisationFeeId(
    orgId: number,
    prevRows: IAcademicFeeLineItem[],
    currRows: IAcademicFeeLineItem[],
): { fee_items: IAcademicFeeLineItem[]; academic_fee_delete_ids: number[] } {
    const fee_items: IAcademicFeeLineItem[] = []
    const academic_fee_delete_ids: number[] = []

    if (!prevRows.length) {
        return { fee_items, academic_fee_delete_ids }
    }
    if (!currRows.length) {
        return { fee_items, academic_fee_delete_ids: [orgId] }
    }

    const template = prevRows[0] ?? currRows[0]
    const prevByGrade = buildGradeToLineMap(prevRows)
    const currByGrade = buildGradeToLineMap(currRows)
    const prevGrades = [...prevByGrade.keys()].sort((left, right) => left - right)
    const currGrades = [...currByGrade.keys()].sort((left, right) => left - right)

    const unchangedGrades: number[] = []
    const changedGrades: number[] = []
    const removedGrades: number[] = []
    const addedGrades: number[] = []

    for (const grade of prevGrades) {
        const prevLine = prevByGrade.get(grade)
        if (!prevLine) continue
        const currLine = currByGrade.get(grade)
        if (!currLine) {
            removedGrades.push(grade)
            continue
        }
        if (Number(prevLine.amount) === Number(currLine.amount)) {
            unchangedGrades.push(grade)
        } else {
            changedGrades.push(grade)
        }
    }

    for (const grade of currGrades) {
        if (!prevByGrade.has(grade)) {
            addedGrades.push(grade)
        }
    }

    if (!changedGrades.length && !removedGrades.length && !addedGrades.length) {
        return { fee_items, academic_fee_delete_ids }
    }

    const isSingleGradeAmountOnlyChange =
        prevGrades.length === 1 && currGrades.length === 1 && changedGrades.length === 1

    if (isSingleGradeAmountOnlyChange) {
        const grade = changedGrades[0]
        fee_items.push(buildPatchFeeLine(orgId, template, [grade], currByGrade.get(grade)!.amount))
        return { fee_items, academic_fee_delete_ids }
    }

    if (unchangedGrades?.length) {
        const remainderAmount = prevByGrade.get(unchangedGrades[0])!.amount
        fee_items.push(buildPatchFeeLine(orgId, template, unchangedGrades, remainderAmount))
    } else {
        academic_fee_delete_ids.push(orgId)
    }

    for (const grade of changedGrades) {
        fee_items.push(buildPostFeeLine(template, grade, currByGrade.get(grade)!.amount))
    }
    for (const grade of addedGrades) {
        fee_items.push(buildPostFeeLine(template, grade, currByGrade.get(grade)!.amount))
    }

    return { fee_items, academic_fee_delete_ids }
}

/**
 * Builds stage PATCH fee payload from per-grade UI rows.
 * When one grade in a bundled DB row changes, splits: PATCH remaining grades on the
 * original organisation_academic_fee_id and POST new rows for edited/added grades.
 */
export function buildFeeItemsStagePatch(
    previousItems: IAcademicFeeLineItem[],
    currentItems: IAcademicFeeLineItem[],
): IFeeItemsStagePatch {
    const wiredPreviousItems = mapFeeItemsForStageApiWire(previousItems)
    const wiredCurrentItems = mapFeeItemsForStageApiWire(currentItems)

    const prevWithoutOrgId: IAcademicFeeLineItem[] = []
    const currWithoutOrgId: IAcademicFeeLineItem[] = []
    for (const item of wiredPreviousItems) {
        if (!isPositiveOrganisationAcademicFeeId(item.organisation_academic_fee_id)) {
            prevWithoutOrgId.push(item)
        }
    }
    for (const item of wiredCurrentItems) {
        if (!isPositiveOrganisationAcademicFeeId(item.organisation_academic_fee_id)) {
            currWithoutOrgId.push(item)
        }
    }

    const prevByOrgId = groupItemsByOrganisationFeeId(wiredPreviousItems)
    const currByOrgId = groupItemsByOrganisationFeeId(wiredCurrentItems)
    const allOrgIds = new Set([...prevByOrgId.keys(), ...currByOrgId.keys()])

    const fee_items: IAcademicFeeLineItem[] = []
    const academic_fee_delete_ids: number[] = []

    for (const orgId of allOrgIds) {
        const bundlePatch = processBundledOrganisationFeeId(
            orgId,
            prevByOrgId.get(orgId) ?? [],
            currByOrgId.get(orgId) ?? [],
        )
        fee_items.push(...bundlePatch.fee_items)
        academic_fee_delete_ids.push(...bundlePatch.academic_fee_delete_ids)
    }

    fee_items.push(...diffFeeItemsForStageApi(prevWithoutOrgId, currWithoutOrgId))

    return {
        fee_items: mapFeeItemsForStageApiWire(fee_items),
        academic_fee_delete_ids: [...new Set(academic_fee_delete_ids)],
    }
}

export function diffFeeItemsForStageApi(
    previousItems: IAcademicFeeLineItem[],
    currentItems: IAcademicFeeLineItem[],
): IAcademicFeeLineItem[] {
    const wiredPreviousItems = mapFeeItemsForStageApiWire(previousItems)
    const wiredCurrentItems = mapFeeItemsForStageApiWire(currentItems)
    const feeLinesByLookupKey = new Map<string, IAcademicFeeLineItem>()
    for (const feeLine of wiredPreviousItems) {
        feeLinesByLookupKey.set(feeItemDiffLookupKey(feeLine), feeLine)
    }
    const changedLines: IAcademicFeeLineItem[] = []
    for (const currentLine of wiredCurrentItems) {
        const baselineFeeLine = feeLinesByLookupKey.get(feeItemDiffLookupKey(currentLine))
        if (!baselineFeeLine) {
            changedLines.push(currentLine)
            continue
        }
        if (feeItemWireCompareKey(currentLine) !== feeItemWireCompareKey(baselineFeeLine)) {
            changedLines.push(currentLine)
        }
    }
    return changedLines
}

export function normalizeStageDataFeeItemsForApi(stageData: IStageData): IStageData {
    const feeItems = stageData.fee_items
    const academicFeeDetails = stageData.academic_fee_details
    if (feeItems?.length && academicFeeDetails?.length && feeItems === academicFeeDetails) {
        const normalized = mapFeeItemsForStageApiWire(feeItems)
        return { ...stageData, fee_items: normalized, academic_fee_details: normalized }
    }
    const next: IStageData = { ...stageData }
    if (feeItems?.length) {
        next.fee_items = mapFeeItemsForStageApiWire(feeItems)
    }
    if (academicFeeDetails?.length) {
        next.academic_fee_details = mapFeeItemsForStageApiWire(academicFeeDetails)
    }
    return next
}

export function toNumberOrNull(value: unknown): number | null {
    const normalized = String(value ?? '')
        .replace(REGEX_PATTERNS.THOUSANDS_SEPARATOR_COMMA_GLOBAL, '')
        .replace(REGEX_PATTERNS.NON_NUMERIC_DECIMAL, '')
        .trim()
    if (!normalized) return null
    const parsedNumber = Number(normalized)
    if (!Number.isFinite(parsedNumber)) return null
    return roundMoneyParsedNumber(parsedNumber)
}

export function formatAnnualFeeDisplayValue(amount: unknown): string {
    const rawDisplay = String(amount ?? '').trim()
    if (!rawDisplay) return ''
    const parsedAmount = toNumberOrNull(amount)
    if (parsedAmount == null) return rawDisplay
    const formatted = parsedAmount.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    })
    return `₹ ${formatted}`
}

export function buildAnnualFeeLineItemsFromS1BoardCards(
    boardCards: IS1BoardCardFeeSource[],
): IAcademicFeeLineItem[] {
    const feeItems: IAcademicFeeLineItem[] = []
    boardCards.forEach((card) => {
        const curriculumId = card.curriculum_id ?? null
        const board = String(card.board || '').trim()
        ;(card.gradeRows || []).forEach((row) => {
            const rowAmountParsed = toNumberOrNull(row.amount)
            if (rowAmountParsed == null || row.grade === undefined || row.grade === '') return
            const rowCurriculumId = row.curriculum_id ?? curriculumId
            const mappedFeeLine: IAcademicFeeLineItem = {
                fee_type: 'annual_fee',
                grades: normalizeFeeItemGradesForApi(String(row.grade)),
                amount: rowAmountParsed,
                curriculum_id: rowCurriculumId ?? null,
            }
            if (isPositiveOrganisationAcademicFeeId(row.organisation_academic_fee_id)) {
                mappedFeeLine.organisation_academic_fee_id = Number(
                    row.organisation_academic_fee_id,
                )
            }
            feeItems.push(mappedFeeLine)
        })
    })
    return feeItems
}

export function getFirstAnnualFeeAmountFromS1BoardCards(
    boardCards: IS1BoardCardFeeSource[],
): number | null {
    const rows: { grade: number; amount: unknown }[] = []
    boardCards.forEach((card) => {
        ;(card.gradeRows || []).forEach((row) => {
            const grade = Number(row.grade)
            if (!Number.isFinite(grade)) return
            rows.push({ grade, amount: row.amount })
        })
    })
    rows.sort((left, right) => left.grade - right.grade)
    for (const row of rows) {
        const parsedAmount = toNumberOrNull(row.amount)
        if (parsedAmount != null) return parsedAmount
    }
    return null
}

export function flattenS1BoardGradeRowsForEmit(
    boardCards: IS1BoardCardFeeSource[],
): ILeadBoardGradeRowValue[] {
    const flattenedRows: ILeadBoardGradeRowValue[] = []
    boardCards.forEach((card) => {
        const board = String(card.board || '').trim()
        const cardCurriculumId = card.curriculum_id ?? null
        ;(card.gradeRows || []).forEach((row) => {
            const grade = Number(row.grade)
            if (!Number.isFinite(grade)) return
            flattenedRows.push({
                grade,
                board: String(row.board || board || ''),
                curriculum_id: row.curriculum_id ?? cardCurriculumId,
                amount: row.amount ?? '',
                studentStrength: row.studentStrength ?? '',
                organisation_academic_fee_id: row.organisation_academic_fee_id ?? null,
            })
        })
    })
    return flattenedRows
}
